import React, { useState, useEffect } from 'react';
import { Card } from '../App';
import { BrewTooltip } from './BrewTooltip';
import type { CompleteAnalysis } from '../lib/wasm-types';
import { cardsSeenByTurn, probabilityLandsByTurn, probabilityAtLeastOne } from '../lib/probability';
import {
  LineChart,
  Line,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

interface ManaCurveChartProps {
  deck: Card[];
  analysis: CompleteAnalysis | null;
  inputMode?: 'search' | 'import';
  activeAnchor?: 'Ideal' | 'A' | 'B' | 'C' | 'D' | 'E';
  onAnchorChange?: (anchor: 'Ideal' | 'A' | 'B' | 'C' | 'D' | 'E') => void;
  // Target parameters for generating anchor even with empty deck
  targetDeckSize?: number;
  targetLands?: number;
  targetRatio?: number;
  // MV filter settings - anchor should respect these
  mvParity?: 'all' | 'odds' | 'evens';
  excludeZero?: boolean;
  // Pre-computed anchor distribution from AnalyticsPanel (same as BUILD tab)
  anchorDistribution?: number[];
  // Land count and total cards for consistency calculations
  landCount?: number;
  totalCards?: number;
  // Play/Draw context for consistency calculations
  onThePlay?: boolean;
}

// MTG Color Identity Palettes - 5 shades per color (light → dark)
const MTG_PALETTES = {
  W: { // White
    name: 'White',
    shades: ['#fef9c3', '#fde68a', '#fcd34d', '#f59e0b', '#d97706'],
    primary: '#fcd34d',
  },
  U: { // Blue
    name: 'Blue',
    shades: ['#a5f3fc', '#67e8f9', '#22d3ee', '#06b6d4', '#0891b2'],
    primary: '#22d3ee',
  },
  B: { // Black
    name: 'Black',
    shades: ['#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'],
    primary: '#8b5cf6',
  },
  R: { // Red
    name: 'Red',
    shades: ['#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c'],
    primary: '#ef4444',
  },
  G: { // Green
    name: 'Green',
    shades: ['#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d'],
    primary: '#22c55e',
  },
  C: { // Colorless (artifacts/lands)
    name: 'Colorless',
    shades: ['#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569'],
    primary: '#94a3b8',
  },
};

// Get color palette based on deck's color identity (WUBRG order)
function getDeckPalette(deck: Card[]): typeof MTG_PALETTES[keyof typeof MTG_PALETTES][] {
  // Check colors in WUBRG order
  const WUBRG = ['W', 'U', 'B', 'R', 'G'] as const;
  const colorSet = new Set<string>();

  deck.forEach(card => {
    card.colors?.forEach(color => {
      if (WUBRG.includes(color as typeof WUBRG[number])) {
        colorSet.add(color);
      }
    });
  });

  // Filter to deck's colors in WUBRG order
  const deckColors = WUBRG.filter(c => colorSet.has(c));

  // If no colors, use colorless
  if (deckColors.length === 0) return [MTG_PALETTES.C];

  // Return palettes in WUBRG order
  return deckColors.map(c => MTG_PALETTES[c]);
}

// Plan colors derived from deck palette
function getPlanColors(deckPalettes: typeof MTG_PALETTES[keyof typeof MTG_PALETTES][]) {
  // Distribute shades across plans
  const plans = ['A', 'B', 'C', 'D', 'E'];
  const colors: Record<string, { stroke: string; name: string }> = {};

  plans.forEach((plan, i) => {
    const paletteIndex = i % deckPalettes.length;
    const shadeIndex = Math.floor(i / deckPalettes.length) % 5;
    const palette = deckPalettes[paletteIndex];
    const matchNum = i + 1; // A=1, B=2, C=3, D=4, E=5

    colors[plan] = {
      stroke: palette.shades[shadeIndex],
      name: `Match ${matchNum}`,
    };
  });

  return colors;
}

// Generate anchor hill distribution (JavaScript implementation matching Rust)
// From formulas.md:
// - μ = W_pos / S_pos (average MV)
// - m* = μ (exact peak, not rounded)
// - Unimodal distribution peaked at m*, exact values
// Respects MV filters: parity (odds/evens) and excludeZero
function generateAnchorHill(
  totalSpells: number,
  totalWeight: number,
  maxMV: number = 7,
  parity: 'all' | 'odds' | 'evens' = 'all',
  excludeZero: boolean = false
): number[] {
  if (totalSpells === 0 || totalWeight === 0) {
    return Array(maxMV + 1).fill(0);
  }

  const avgMV = totalWeight / totalSpells;

  // Build exact distribution using Gaussian-like shape centered on exact avgMV
  const distribution = Array(maxMV + 1).fill(0);

  // Use a Gaussian distribution centered on avgMV
  // σ (spread) based on how much weight we need to distribute
  const sigma = Math.max(1.0, avgMV * 0.4); // Spread proportional to average

  // Helper: is this MV valid for current filters?
  const isValidMV = (mv: number): boolean => {
    if (excludeZero && mv === 0) return false;
    if (parity === 'odds' && mv % 2 === 0) return false;
    if (parity === 'evens' && mv % 2 === 1) return false;
    return true;
  };

  // Calculate raw Gaussian weights for each VALID MV
  const weights: number[] = [];
  let totalWeight2 = 0;

  for (let mv = 1; mv <= maxMV; mv++) {
    if (!isValidMV(mv)) {
      weights[mv] = 0;
      continue;
    }
    // Gaussian: exp(-0.5 * ((x - μ) / σ)^2)
    const z = (mv - avgMV) / sigma;
    const w = Math.exp(-0.5 * z * z);
    weights[mv] = w;
    totalWeight2 += w;
  }

  // Normalize to total spells (only across valid MVs)
  if (totalWeight2 > 0) {
    for (let mv = 1; mv <= maxMV; mv++) {
      distribution[mv] = (weights[mv] / totalWeight2) * totalSpells;
    }
  }

  return distribution;
}

export function ManaCurveChart({ deck, analysis, inputMode = 'search', activeAnchor: controlledAnchor, onAnchorChange, targetDeckSize = 60, targetLands = 24, targetRatio = 3, mvParity = 'all', excludeZero = false, anchorDistribution, landCount: propLandCount, totalCards: propTotalCards, onThePlay: propOnThePlay = true }: ManaCurveChartProps) {
  // Derive land count and total cards from props or deck
  const effectiveLandCount = propLandCount ?? deck.filter(c => c.isLand).reduce((sum, c) => sum + c.quantity, 0);
  const effectiveTotalCards = propTotalCards ?? deck.reduce((sum, c) => sum + c.quantity, 0);
  // Get deck's color palette and derive plan colors
  const deckPalettes = getDeckPalette(deck);
  const PLAN_COLORS = getPlanColors(deckPalettes);

  // Plans A-E are always available (they represent match-specific quantity transformations)
  // Show toggle buttons even with empty deck for visual consistency
  const availablePlans = ['A', 'B', 'C', 'D', 'E'];

  // State for selected plans (which plan curves to show)
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);

  // Single-select anchor - which anchor curve to compare against
  const [activeAnchor, setActiveAnchor] = useState<'Ideal' | 'A' | 'B' | 'C' | 'D' | 'E'>('Ideal');

  // State for showing Your Deck curve
  const [showYourDeck, setShowYourDeck] = useState(true);

  // View mode: 'qty' = card counts, 'wt' = mana weight (MV × count)
  const [viewMode, setViewMode] = useState<'qty' | 'wt'>('qty');

  // Show anchor line when an anchor is selected
  const showAnchors = true; // Always show when activeAnchor is set
  const [showDeltaArea, setShowDeltaArea] = useState(true); // Show heat band
  const [showGoldLine, setShowGoldLine] = useState(false); // Show total average "gold" anchor

  // Note: activeAnchor determines which anchor line is shown

  const togglePlan = (plan: string) => {
    setSelectedPlans(prev =>
      prev.includes(plan)
        ? prev.filter(p => p !== plan)
        : [...prev, plan]
    );
  };
  // Calculate mana curve data including plan-specific curves
  const curveData = () => {
    // Always calculate actual curve from deck - DON'T cap yet, we need true max
    // Track both counts and weights
    const actualCounts: Record<number, number> = {};
    const actualWeights: Record<number, number> = {};
    let maxActualMV = 0;
    deck.forEach(card => {
      if (!card.isLand) {
        const mv = card.effectiveMV ?? card.cmc;
        const roundedMV = Math.round(mv);
        maxActualMV = Math.max(maxActualMV, roundedMV);
        actualCounts[roundedMV] = (actualCounts[roundedMV] || 0) + card.quantity;
        actualWeights[roundedMV] = (actualWeights[roundedMV] || 0) + (roundedMV * card.quantity);
      }
    });

    // Use counts or weights based on viewMode
    const actualValues = viewMode === 'wt' ? actualWeights : actualCounts;

    // idealCurve is populated below based on activeAnchor

    // Calculate plan-specific metrics for anchor generation (matches OverviewTab logic)
    const calcPlanMetrics = (planKey: 'A' | 'B' | 'C' | 'D' | 'E') => {
      let totalWeight = 0;
      let totalSpells = 0;
      let maxMV = 0;

      deck.forEach(card => {
        if (card.isLand) return;

        const copySideboard = card.planCopySideboard?.[planKey] || [];
        const copyMVs = card.planCopyMVs?.[planKey];
        const baseMV = card.effectiveMV ?? card.cmc;

        // Iterate through each copy of the card
        for (let i = 0; i < card.quantity; i++) {
          const isFlagged = copySideboard[i] ?? false;

          // For mainboard cards: isFlagged=true means OUT (sideboarded)
          // For sideboard cards: isFlagged=true means IN (mainboarded)
          const isInMainboard = card.isSideboard ? isFlagged : !isFlagged;

          if (!isInMainboard) continue;

          // This copy is in mainboard for this plan
          totalSpells += 1;
          const mv = copyMVs?.[i] ?? baseMV;
          totalWeight += mv;
          maxMV = Math.max(maxMV, Math.round(mv));
        }
      });

      return { totalWeight, totalSpells, maxMV };
    };

    // Generate the single active anchor curve
    const idealCurve: Record<number, number> = {};

    // Helper to check if a plan has any changes from base (sideboard moves OR MV overrides)
    const planHasChanges = (planKey: 'A' | 'B' | 'C' | 'D' | 'E'): boolean => {
      return deck.some(card => {
        // Check for sideboard changes
        const copySideboard = card.planCopySideboard?.[planKey] || [];
        const hasSideboardChange = copySideboard.some((flag: boolean) => flag === true);
        if (hasSideboardChange) return true;

        // Check for MV overrides
        const copyMVs = card.planCopyMVs?.[planKey] || [];
        const baseMV = card.effectiveMV ?? card.cmc;
        const hasMVChange = copyMVs.some((mv: number) => mv !== undefined && mv !== baseMV);
        if (hasMVChange) return true;

        // Check for face overrides (MDFC)
        const copyFaces = card.planCopyFace?.[planKey] || [];
        const hasFaceChange = copyFaces.some((face: number) => face !== undefined && face !== 0);
        if (hasFaceChange) return true;

        return false;
      });
    };

    if (activeAnchor === 'Ideal') {
      // Base anchor - use anchorDistribution prop if available (matches BUILD tab),
      // fallback to WASM, then generate from target parameters
      if (anchorDistribution && anchorDistribution.length > 0) {
        // Use pre-computed DECKSTR anchor from AnalyticsPanel (same as BUILD tab)
        anchorDistribution.forEach((count, mv) => {
          if (count > 0) {
            idealCurve[mv] = count;
          }
        });
      } else if (analysis?.anchor_hill?.distribution && Array.isArray(analysis.anchor_hill.distribution)) {
        // Fallback to WASM-calculated anchor
        analysis.anchor_hill.distribution.forEach((count, index) => {
          const mv = index + 1;
          if (mv <= 7 && count > 0) {
            idealCurve[mv] = count;
          }
        });
      } else {
        // Generate from target parameters (deck empty or no WASM)
        const targetNonlands = targetDeckSize - targetLands;
        const targetWeight = Math.round(targetNonlands * targetRatio);
        if (targetNonlands > 0 && targetWeight > 0) {
          const distribution = generateAnchorHill(targetNonlands, targetWeight, 7, mvParity, excludeZero);
          distribution.forEach((count, mv) => {
            if (mv >= 1 && count > 0) {
              idealCurve[mv] = count;
            }
          });
        }
      }
    } else {
      // Plan-specific anchor (A-E)
      // If plan has no changes, use the SAME anchor as Base for consistency
      const hasChanges = planHasChanges(activeAnchor);

      if (!hasChanges && anchorDistribution && anchorDistribution.length > 0) {
        // No changes - use same anchor as Base
        anchorDistribution.forEach((count, mv) => {
          if (count > 0) {
            idealCurve[mv] = count;
          }
        });
      } else {
        // Has sideboard changes - calculate plan-specific anchor
        const metrics = calcPlanMetrics(activeAnchor);
        if (metrics.totalSpells > 0) {
          const distribution = generateAnchorHill(metrics.totalSpells, metrics.totalWeight, Math.max(7, metrics.maxMV), mvParity, excludeZero);
          distribution.forEach((count, mv) => {
            if (mv >= 1 && count > 0) {
              idealCurve[mv] = count;
            }
          });
        }
      }
    }

    // Get stability report data if available
    const stabilityByMV: Record<number, { zone: string; tolerance: number }> = {};
    if (analysis?.stability_report?.per_mv_status) {
      analysis.stability_report.per_mv_status.forEach(status => {
        stabilityByMV[status.mv] = { zone: status.zone, tolerance: status.tolerance };
      });
    }

    // Calculate plan-specific curves using per-copy sideboard flags
    const planCurves: Record<string, Record<number, number>> = {};

    selectedPlans.forEach(plan => {
      planCurves[plan] = {};
      deck.forEach(card => {
        if (!card.isLand) {
          const planKey = plan as 'A' | 'B' | 'C' | 'D' | 'E';
          const copyMVs = card.planCopyMVs?.[planKey];
          const copySideboard = card.planCopySideboard?.[planKey] || [];
          const baseMV = card.effectiveMV ?? card.cmc;

          // Iterate over each copy
          for (let i = 0; i < card.quantity; i++) {
            const isFlagged = copySideboard[i] ?? false;

            // For mainboard cards: isFlagged=true means OUT (sideboarded)
            // For sideboard cards: isFlagged=true means IN (mainboarded)
            const isInMainboard = card.isSideboard ? isFlagged : !isFlagged;

            if (!isInMainboard) continue;

            // Get the MV for this copy
            const copyMV = copyMVs?.[i] ?? baseMV;
            const mv = Math.round(copyMV); // NO CAP - allow high MVs
            // For weight mode, add MV; for qty mode, add 1
            const valueToAdd = viewMode === 'wt' ? mv : 1;
            planCurves[plan][mv] = (planCurves[plan][mv] || 0) + valueToAdd;
          }
        }
      });
    });

    // Calculate adaptive max MV (highest in deck, anchor, or plans)
    // Minimum of 5 for usability, but extends to actual max if higher
    const anchorMaxMV = Object.keys(idealCurve).reduce((max, mv) => Math.max(max, parseInt(mv)), 0);
    const planMaxMV = Object.values(planCurves).reduce((max, curve) => {
      const curveMax = Object.keys(curve).reduce((m, mv) => Math.max(m, parseInt(mv)), 0);
      return Math.max(max, curveMax);
    }, 0);
    const adaptiveMaxMV = Math.max(5, maxActualMV, anchorMaxMV, planMaxMV);

    // Store adaptiveMaxMV for the '+' label (last bucket always gets '+')
    const maxMVForLabel = adaptiveMaxMV;

    // Calculate Gold Line - average of ALL highlighted curves per-MV
    // Includes: Anchor (if visible), Your Deck (if visible), Selected Plans
    // This creates a precise average at each MV point, not a generated anchor hill
    const goldCurve: Record<number, number> = {};

    // Check if any curve has 0-drop cards — skip MV 0 if empty to save chart space
    const hasMV0 = (actualValues[0] || 0) > 0 ||
      Object.values(planCurves).some(curve => (curve[0] || 0) > 0);
    const startMV = hasMV0 ? 0 : 1;

    // Build data array from startMV to adaptiveMaxMV
    // We'll calculate gold line during this loop since we need all curve values
    const data = [];
    for (let mv = startMV; mv <= adaptiveMaxMV; mv++) {
      const actual = actualValues[mv] || 0;
      const idealBase = idealCurve[mv] || 0; // Will be 0 for MV 0 (no 0-cost spells in anchor)
      // For weight mode, multiply ideal by MV
      const ideal = viewMode === 'wt' ? idealBase * mv : idealBase;
      const delta = actual - ideal;

      // Calculate gold line value - average of ALL visible curves at this MV
      const goldValues: number[] = [];

      // 1. Include anchor (always visible when showAnchors is true)
      if (ideal > 0) {
        goldValues.push(ideal);
      }

      // 2. Include Your Deck actual (when showYourDeck is true)
      // Note: showYourDeck state isn't available here, so we'll always include it 
      // when there's data, and filter in the render
      if (actual > 0 || deck.length > 0) {
        goldValues.push(actual);
      }

      // 3. Include selected plan values
      selectedPlans.forEach(plan => {
        const planVal = planCurves[plan]?.[mv] || 0;
        goldValues.push(planVal);
      });

      // Calculate average (only if we have values)
      const goldAvg = goldValues.length > 0
        ? goldValues.reduce((sum, v) => sum + v, 0) / goldValues.length
        : 0;
      goldCurve[mv] = goldAvg;

      // Get zone status from stability report, or calculate fallback
      const stabilityInfo = stabilityByMV[mv];
      const zone = stabilityInfo?.zone || (Math.abs(delta) <= 1 ? 'Green' : Math.abs(delta) <= 2 ? 'Yellow' : 'Red');
      const tolerance = stabilityInfo?.tolerance || (mv <= 2 ? 2 : mv <= 4 ? 3 : 4);

      // Calculate tolerance band boundaries for heat gradient
      // Green zone: within 50% of tolerance
      // Yellow zone: within 100% of tolerance
      // Red zone: beyond tolerance
      const greenHalf = tolerance * 0.5;

      // --- CONSISTENCY LAYER ---
      // Calculate per-MV playability using hypergeometric probability
      // P(cast on curve) = P(≥ MV lands by turn MV)
      // P(draw) = P(see ≥1 copy by turn MV) based on copies at this MV
      // P(playable) = P(cast) × P(draw)
      let castability = 0;
      let drawability = 0;
      let playability = 0;
      const copiesAtMV = actualCounts[mv] || 0;

      if (mv >= 1 && effectiveTotalCards > 0 && effectiveLandCount > 0) {
        const seen = cardsSeenByTurn(mv, propOnThePlay);
        castability = probabilityLandsByTurn(effectiveTotalCards, effectiveLandCount, mv, seen);

        if (copiesAtMV > 0) {
          drawability = probabilityAtLeastOne(effectiveTotalCards, copiesAtMV, seen);
          playability = castability * drawability;
        }
      }

      // Consistency zone based on castability (the user's agency — tells them what's possible)
      const consistencyZone = castability >= 0.7 ? 'reliable' : castability >= 0.5 ? 'moderate' : castability >= 0.3 ? 'marginal' : 'unreliable';

      const dataPoint: any = {
        mv: mv === maxMVForLabel ? `${mv}+` : mv.toString(),
        mvNum: mv,
        actual,
        ideal: ideal > 0 ? parseFloat(ideal.toFixed(1)) : 0,
        delta: parseFloat(delta.toFixed(1)),
        zone,
        tolerance,
        hasWarning: zone === 'Red',

        // Consistency data
        castability: Math.round(castability * 100),   // % chance of having MV lands by turn MV
        drawability: Math.round(drawability * 100),   // % chance of drawing ≥1 copy by turn MV
        playability: Math.round(playability * 100),   // combined on-curve playability
        copiesAtMV,
        consistencyZone,

        // Tolerance bands around the anchor (for heat gradient visualization)
        // Green zone bounds (closest to anchor)
        greenUpper: ideal + greenHalf,
        greenLower: Math.max(0, ideal - greenHalf),
        // Yellow zone bounds
        yellowUpper: ideal + tolerance,
        yellowLower: Math.max(0, ideal - tolerance),
        // Red zone starts beyond tolerance
        redUpper: ideal + tolerance * 1.5,
        redLower: Math.max(0, ideal - tolerance * 1.5),
      };

      // Add plan-specific counts AND delta from both Anchor and Plan A
      selectedPlans.forEach(plan => {
        const planCount = planCurves[plan][mv] || 0;
        dataPoint[`plan${plan}`] = planCount;

        // Delta from Anchor (ideal curve)
        const deltaAnchor = ideal > 0 ? planCount - ideal : 0;
        dataPoint[`plan${plan}DeltaAnchor`] = parseFloat(deltaAnchor.toFixed(1));

        // Excess ONLY - just the gap height (plan - ideal) when positive
        // This will be stacked on top of ideal baseline
        const excessDelta = (ideal > 0 && deltaAnchor > 0) ? deltaAnchor : 0;
        dataPoint[`plan${plan}ExcessDelta`] = excessDelta;

        // Delta from Plan A (actual deck curve)
        dataPoint[`plan${plan}DeltaA`] = parseFloat((planCount - actual).toFixed(1));
      });

      // Actual deck excess - just the gap height (actual - ideal) when positive
      // This will be stacked on top of ideal baseline
      const actualExcess = actual - ideal;
      dataPoint.actualExcessDelta = (ideal > 0 && actualExcess > 0) ? actualExcess : 0;

      // Gold Line - proper anchor generated from averaged deck metrics
      // This is the "anchor's anchor" - if all plans converge here, the deck is perfectly tuned
      dataPoint.goldLine = goldCurve[mv] || 0;

      data.push(dataPoint);
    }

    return data;
  };

  const data = curveData();

  // Note: We no longer show "empty state" - we can display the target anchor even with 0 cards

  // Check if we have ideal anchor data
  const hasIdealData = data.some(d => d.ideal > 0);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Zone color mapping
      const zoneColors: Record<string, { bg: string; text: string; label: string }> = {
        Green: { bg: 'bg-green-500', text: 'text-green-400', label: 'Stable' },
        Yellow: { bg: 'bg-yellow-500', text: 'text-yellow-400', label: 'Caution' },
        Red: { bg: 'bg-red-500', text: 'text-red-400', label: 'Unstable' },
      };
      const zoneInfo = zoneColors[data.zone] || zoneColors.Green;

      return (
        <div className="bg-[#0f172a] border border-purple-500/50 rounded-lg p-3 shadow-[0_0_20px_rgba(0,0,0,0.8)]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-white font-semibold">Mana Value: {data.mv}</p>
            {data.zone && (
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded ${data.zone === 'Green' ? 'bg-green-500/20' : data.zone === 'Yellow' ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                <div className={`w-2 h-2 rounded-full ${zoneInfo.bg}`}></div>
                <span className={`text-xs ${zoneInfo.text}`}>{zoneInfo.label}</span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            {showYourDeck && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                <span className="text-purple-400 text-sm">Your Deck: {data.actual}</span>
              </div>
            )}
            {showAnchors && hasIdealData && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-pink-400"></div>
                <span className="text-pink-400 text-sm">Anchor: {data.ideal}</span>
              </div>
            )}
            {selectedPlans.map((plan: string) => {
              const planCount = data[`plan${plan}`] || 0;
              const deltaAnchor = data[`plan${plan}DeltaAnchor`] || 0;
              const deltaA = data[`plan${plan}DeltaA`] || 0;
              const planColor = PLAN_COLORS[plan as keyof typeof PLAN_COLORS];

              return (
                <div key={plan} className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: planColor?.stroke }}
                    ></div>
                    <span
                      className="text-sm"
                      style={{ color: planColor?.stroke }}
                    >
                      {planColor?.name}: {planCount}
                    </span>
                  </div>
                  {/* Show plan deltas when comparing to something */}
                  {(showAnchors || showYourDeck) && plan !== 'A' && (
                    <div className="ml-5 flex gap-3 text-xs">
                      {showAnchors && hasIdealData && (
                        <span className={deltaAnchor > 0 ? 'text-yellow-400' : deltaAnchor < 0 ? 'text-blue-400' : 'text-green-400'}>
                          Δ Anchor: {deltaAnchor > 0 ? '+' : ''}{deltaAnchor}
                        </span>
                      )}
                      {showYourDeck && (
                        <span className={deltaA > 0 ? 'text-amber-400' : deltaA < 0 ? 'text-purple-400' : 'text-green-400'}>
                          Δ A: {deltaA > 0 ? '+' : ''}{deltaA}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {showYourDeck && showAnchors && hasIdealData && (
              <div className="border-t border-white/10 mt-2 pt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${data.delta > 0 ? 'text-yellow-400' : data.delta < 0 ? 'text-blue-400' : 'text-green-400'}`}>
                    Deck Δ Anchor: {data.delta > 0 ? '+' : ''}{data.delta}
                  </span>
                  {data.tolerance && (
                    <span className="text-xs text-white/40">
                      ±{data.tolerance}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Consistency Layer — castability & draw probability */}
            {data.mvNum >= 1 && data.castability > 0 && (
              <div className="border-t border-white/10 mt-2 pt-2 space-y-1">
                <div className="text-xs font-medium text-white/70 mb-1">On-Curve Consistency (T{data.mvNum})</div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">P(≥{data.mvNum} lands)</span>
                  <span className={`text-xs font-mono ${data.castability >= 70 ? 'text-green-400' : data.castability >= 50 ? 'text-yellow-400' : data.castability >= 30 ? 'text-orange-400' : 'text-red-400'}`}>
                    {data.castability}%
                  </span>
                </div>
                {data.copiesAtMV > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/50">P(draw {data.copiesAtMV === 1 ? 'the 1-of' : `≥1 of ${data.copiesAtMV}`})</span>
                      <span className={`text-xs font-mono ${data.drawability >= 70 ? 'text-green-400' : data.drawability >= 50 ? 'text-yellow-400' : data.drawability >= 30 ? 'text-orange-400' : 'text-red-400'}`}>
                        {data.drawability}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/5 pt-1">
                      <span className="text-xs text-white/70 font-medium">Playable on curve</span>
                      <span className={`text-xs font-mono font-bold ${data.playability >= 50 ? 'text-green-400' : data.playability >= 30 ? 'text-yellow-400' : data.playability >= 15 ? 'text-orange-400' : 'text-red-400'}`}>
                        {data.playability}%
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full gap-2">
      {/* Info: No ideal data */}
      {!hasIdealData && deck.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2">
          <div className="text-blue-400 text-xs flex items-center gap-2">
            <span>ℹ️</span>
            <span>Ideal anchor curve will appear once deck analysis completes</span>
          </div>
        </div>
      )}

      {/* Composed Chart with Line and Area - fills available space */}
      <div className="flex-1 min-h-0 bg-white/5 rounded-lg p-2 border border-white/5">
        <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={150}>
          <ComposedChart
            data={data}
            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          >
            <defs>
              {/* Gap fill gradient using deck's color identity */}
              <linearGradient id="gapFillGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={deckPalettes[0]?.shades[0] || '#a855f7'} stopOpacity={0.6} />
                <stop offset="50%" stopColor={deckPalettes[Math.min(1, deckPalettes.length - 1)]?.primary || '#8b5cf6'} stopOpacity={0.4} />
                <stop offset="100%" stopColor={deckPalettes[Math.min(2, deckPalettes.length - 1)]?.shades[4] || '#ec4899'} stopOpacity={0.3} />
              </linearGradient>
              {/* Glow effect using deck's primary color */}
              <linearGradient id="glowGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={deckPalettes[0]?.primary || '#a855f7'} stopOpacity={0.8} />
                <stop offset="100%" stopColor={deckPalettes[0]?.primary || '#a855f7'} stopOpacity={0.1} />
              </linearGradient>
              {/* Background for ideal coverage - matches chart bg (bg-white/5 on #0f172a) */}
              <linearGradient id="idealCoverGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#141B2B" stopOpacity={1} />
                <stop offset="100%" stopColor="#141B2B" stopOpacity={1} />
              </linearGradient>
              {/* Dynamic gradients for each plan - opacity decreases with more plans */}
              {selectedPlans.map((plan, idx) => {
                const planColor = PLAN_COLORS[plan as keyof typeof PLAN_COLORS];
                if (!planColor) return null;
                // Specific opacity values: 1 plan=40%, 2=35%, 3=25%, 4=20%, 5=15%
                const opacityMap: Record<number, number> = { 1: 0.40, 2: 0.35, 3: 0.25, 4: 0.20, 5: 0.15 };
                const baseOpacity = opacityMap[selectedPlans.length] || 0.15;
                return (
                  <linearGradient key={plan} id={`planGradient${plan}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={planColor.stroke} stopOpacity={baseOpacity * 1.2} />
                    <stop offset="50%" stopColor={planColor.stroke} stopOpacity={baseOpacity} />
                    <stop offset="100%" stopColor={planColor.stroke} stopOpacity={baseOpacity * 0.6} />
                  </linearGradient>
                );
              })}
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey="mv"
              stroke="rgba(255,255,255,0.6)"
              tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 12 }}
              label={{ value: 'Mana Value', position: 'insideBottom', offset: -5, fill: 'rgba(255,255,255,0.6)' }}
            />
            <YAxis
              stroke="rgba(255,255,255,0.6)"
              tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 12 }}
              label={{ value: viewMode === 'wt' ? 'Mana Weight' : 'Card Count', angle: -90, position: 'insideLeft', fill: 'rgba(255,255,255,0.6)' }}
            />
            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 1000 }}
            />
            {/* Legend hidden - controls bar provides same info */}

            {/* Gap Fill Heat Band - Draw deck area, then cover with anchor */}
            {showYourDeck && showAnchors && hasIdealData && showDeltaArea && (
              <>
                {/* Full deck area from 0 to actual */}
                <Area
                  type="monotone"
                  dataKey="actual"
                  stroke="none"
                  fill="url(#gapFillGradient)"
                  fillOpacity={1}
                  legendType="none"
                  isAnimationActive={true}
                  animationDuration={800}
                  animationEasing="ease-out"
                />
                {/* Cover from 0 to ideal (OPAQUE) - hides everything below anchor */}
                <Area
                  type="monotone"
                  dataKey="ideal"
                  stroke="none"
                  fill="#141B2B"
                  fillOpacity={1}
                  legendType="none"
                  isAnimationActive={true}
                  animationDuration={800}
                  animationEasing="ease-out"
                />
              </>
            )}

            {/* Your Deck Line - with zone-colored dots + consistency opacity */}
            {showYourDeck && (
              <Line
                type="monotone"
                dataKey="actual"
                name="Your Deck (A)"
                stroke="#a855f7"
                strokeWidth={3}
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  // Color based on zone status
                  const zoneColors: Record<string, string> = {
                    Green: '#10b981',
                    Yellow: '#f59e0b',
                    Red: '#ef4444'
                  };
                  const color = zoneColors[payload.zone] || '#a855f7';

                  // Consistency-based opacity: dim dots where castability is low
                  // MV 0 has no castability concept, keep full opacity
                  const cast = payload.castability || 0;
                  const dotOpacity = payload.mvNum === 0 ? 1 : cast >= 70 ? 1 : cast >= 50 ? 0.85 : cast >= 30 ? 0.65 : 0.4;

                  return (
                    <g>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={6}
                        fill={color}
                        stroke="#fff"
                        strokeWidth={2}
                        opacity={dotOpacity}
                      />
                      {/* Small castability % label below dot for MVs with actual cards */}
                      {payload.mvNum >= 2 && payload.actual > 0 && payload.castability > 0 && (
                        <text
                          x={cx}
                          y={cy + 16}
                          textAnchor="middle"
                          fill={cast >= 70 ? '#10b981' : cast >= 50 ? '#f59e0b' : cast >= 30 ? '#fb923c' : '#ef4444'}
                          fontSize={9}
                          fontFamily="monospace"
                          opacity={0.8}
                        >
                          {cast}%
                        </text>
                      )}
                    </g>
                  );
                }}
                activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff' }}
              />
            )}

            {/* Ideal Anchor Line - Pink with glow effect (only if data exists and toggle is on) */}
            {showAnchors && hasIdealData && (
              <Line
                type="monotone"
                dataKey="ideal"
                name="Ideal Anchor"
                stroke="#f472b6"
                strokeWidth={3}
                strokeDasharray="5 5"
                dot={{ fill: '#f472b6', strokeWidth: 2, r: 5 }}
                activeDot={{ r: 7, strokeWidth: 2, stroke: '#ec4899' }}
              />
            )}

            {/* Gold Line - Average anchor across all plans (the "anchor's anchor") */}
            {showGoldLine && hasIdealData && (
              <>
                {/* Gold Area Fill - 10% opacity */}
                <Area
                  type="monotone"
                  dataKey="goldLine"
                  stroke="none"
                  fill="#f59e0b"
                  fillOpacity={0.1}
                  legendType="none"
                  isAnimationActive={true}
                  animationDuration={600}
                />
                <Line
                  type="monotone"
                  dataKey="goldLine"
                  name="Total Average"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  dot={{ fill: '#f59e0b', strokeWidth: 1, r: 4 }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: '#d97706' }}
                />
              </>
            )}

            {/* Plan Lines with Gap Fill - Different colors for each plan */}
            {selectedPlans.map(plan => {
              const planColor = PLAN_COLORS[plan as keyof typeof PLAN_COLORS];
              if (!planColor) return null;

              return (
                <React.Fragment key={plan}>
                  {/* Plan area fill - Draw plan area, then cover with anchor */}
                  {showDeltaArea && hasIdealData && (
                    <>
                      {/* Full plan area from 0 to plan - uses gradient with dynamic opacity */}
                      <Area
                        type="monotone"
                        dataKey={`plan${plan}`}
                        stroke="none"
                        fill={`url(#planGradient${plan})`}
                        fillOpacity={1}
                        legendType="none"
                        isAnimationActive={true}
                        animationDuration={600}
                      />
                      {/* Cover from 0 to ideal (OPAQUE) - hides below anchor */}
                      <Area
                        type="monotone"
                        dataKey="ideal"
                        stroke="none"
                        fill="#141B2B"
                        fillOpacity={1}
                        legendType="none"
                        isAnimationActive={false}
                      />
                    </>
                  )}
                  {/* Line for this plan — with count labels on dots */}
                  <Line
                    type="monotone"
                    dataKey={`plan${plan}`}
                    name={planColor.name}
                    stroke={planColor.stroke}
                    strokeWidth={2.5}
                    strokeDasharray="3 3"
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      const count = payload[`plan${plan}`] || 0;
                      return (
                        <g>
                          <circle cx={cx} cy={cy} r={4} fill={planColor.stroke} stroke="#fff" strokeWidth={1.5} />
                          {count > 0 && (
                            <text x={cx} y={cy - 10} textAnchor="middle" fill={planColor.stroke} fontSize={9} fontWeight={700} fontFamily="Inter, sans-serif" opacity={0.9}>
                              {count}
                            </text>
                          )}
                        </g>
                      );
                    }}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: planColor.stroke }}
                  />
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Compact Controls Bar - scrollable on mobile */}
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-0.5 scrollbar-thin flex-shrink-0">
        {/* Base toggles */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* View Mode Toggle - Weights vs Quantities */}
          <div className="flex items-center bg-white/5 rounded-full border border-white/10 overflow-hidden flex-shrink-0">
            <BrewTooltip
              title="Card Quantities View"
              badge="Qty"
              description="Plots the number of nonland spell copies at each mana value against the target anchor curve."
              accent="cyan"
            >
              <button
                type="button"
                onClick={() => setViewMode('qty')}
                className={`px-2 py-1 text-[10px] sm:text-xs font-medium transition-all ${viewMode === 'qty'
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'text-white/40 hover:text-white/60'
                  }`}
              >
                Qty
              </button>
            </BrewTooltip>
            <BrewTooltip
              title="Mana Weight View"
              badge="MV × Qty"
              description="Plots total mana weight (Mana Value × Copies) contributed by each drop slot."
              accent="amber"
            >
              <button
                type="button"
                onClick={() => setViewMode('wt')}
                className={`px-2 py-1 text-[10px] sm:text-xs font-medium transition-all ${viewMode === 'wt'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'text-white/40 hover:text-white/60'
                  }`}
              >
                Wt
              </button>
            </BrewTooltip>
          </div>

          <BrewTooltip
            title="Toggle Deck Curve"
            badge={showYourDeck ? 'Visible' : 'Hidden'}
            description="Shows or hides your current mainboard spell curve line on the chart."
            accent="purple"
          >
            <button
              type="button"
              onClick={() => setShowYourDeck(!showYourDeck)}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium transition-all whitespace-nowrap ${showYourDeck
                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40'
                : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
                }`}
            >
              Your Deck
            </button>
          </BrewTooltip>

          {/* Anchor dropdown - single select */}
          <BrewTooltip
            title="Reference Anchor Curve"
            badge={activeAnchor === 'Ideal' ? 'Base' : activeAnchor}
            description="Selects which target curve (Base or Matchup M1–M5) is plotted as the ideal reference line."
            accent="cyan"
          >
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[10px] text-white/40 hidden sm:inline">ANCHOR:</span>
              <select
                value={activeAnchor}
                onChange={(e) => setActiveAnchor(e.target.value as 'Ideal' | 'A' | 'B' | 'C' | 'D' | 'E')}
                className="px-2 py-0.5 rounded text-[10px] font-medium bg-white/10 text-white border border-white/20 cursor-pointer hover:bg-white/15 transition-colors"
                style={{ appearance: 'none', paddingRight: '1.5rem', backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.25rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1rem 1rem' }}
              >
                <option value="Ideal" className="bg-gray-800 text-white">Base</option>
                <option value="A" className="bg-gray-800 text-white">M1</option>
                <option value="B" className="bg-gray-800 text-white">M2</option>
                <option value="C" className="bg-gray-800 text-white">M3</option>
                <option value="D" className="bg-gray-800 text-white">M4</option>
                <option value="E" className="bg-gray-800 text-white">M5</option>
              </select>
            </div>
          </BrewTooltip>

          {hasIdealData && showYourDeck && (
            <BrewTooltip
              title="Delta Heat Band"
              badge={showDeltaArea ? 'On' : 'Off'}
              description="Shades the gap between your actual deck curve and the target anchor curve."
              accent="purple"
            >
              <button
                type="button"
                onClick={() => setShowDeltaArea(!showDeltaArea)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium transition-all flex-shrink-0 ${showDeltaArea
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40'
                  : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
                  }`}
              >
                Heat
              </button>
            </BrewTooltip>
          )}

          {hasIdealData && (
            <BrewTooltip
              title="Golden Average Curve"
              badge={showGoldLine ? 'On' : 'Off'}
              description="Overlays the combined average anchor curve across your base deck and configured matchup plans."
              accent="amber"
            >
              <button
                type="button"
                onClick={() => setShowGoldLine(!showGoldLine)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium transition-all flex-shrink-0 ${showGoldLine
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                  : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
                  }`}
              >
                Gold
              </button>
            </BrewTooltip>
          )}
        </div>

        {/* Separator */}
        <div className="h-4 sm:h-6 w-px bg-white/10 flex-shrink-0"></div>

        {/* Match Pills - for showing curve lines */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <BrewTooltip
            title="Matchup Plan Overlays"
            description="Toggle individual post-sideboard matchup curves (M1–M5) on the chart."
            accent="slate"
          >
            <span className="text-[10px] text-white/40 hidden sm:inline mr-1 cursor-help">MATCHES:</span>
          </BrewTooltip>
          {availablePlans.map((plan, idx) => {
            const matchNum = idx + 1;
            const planColor = PLAN_COLORS[plan as keyof typeof PLAN_COLORS];
            if (!planColor) return null;
            const isShowing = selectedPlans.includes(plan);
            const isAnchorSelected = activeAnchor === plan;

            return (
              <BrewTooltip
                key={plan}
                title={`Match ${matchNum} Curve`}
                badge={isShowing ? 'Visible' : 'Hidden'}
                description={`Toggle your Match ${matchNum} post-sideboard curve line on the chart.`}
                accent="purple"
              >
                <button
                  type="button"
                  onClick={() => togglePlan(plan)}
                  className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium transition-all flex items-center gap-1 ${isShowing
                    ? 'border'
                    : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
                    }`}
                  style={isShowing ? {
                    backgroundColor: `${planColor.stroke}20`,
                    color: planColor.stroke,
                    borderColor: `${planColor.stroke}50`
                  } : undefined}
                >
                  {isAnchorSelected && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                  {matchNum}
                </button>
              </BrewTooltip>
            );
          })}
        </div>

        {/* Quick actions */}
        <div className="flex items-center gap-1 ml-auto flex-shrink-0">
          <BrewTooltip
            title="Show All Match Curves"
            description="Overlays all 5 matchup curves (M1–M5) simultaneously."
            accent="slate"
          >
            <button
              type="button"
              onClick={() => setSelectedPlans(availablePlans)}
              className="px-1.5 py-0.5 rounded text-[10px] text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors"
            >
              All
            </button>
          </BrewTooltip>
          <BrewTooltip
            title="Hide All Match Curves"
            description="Hides all M1–M5 matchup curve overlays."
            accent="slate"
          >
            <button
              type="button"
              onClick={() => setSelectedPlans([])}
              className="px-1.5 py-0.5 rounded text-[10px] text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors"
            >
              None
            </button>
          </BrewTooltip>
        </div>
      </div>
    </div>
  );
}
